import { useEffect, useMemo, useState } from "react";

function formatDate(dateValue) {
  try {
    return new Date(dateValue).toLocaleDateString();
  } catch (_error) {
    return dateValue;
  }
}

function monthTitle(dateObj) {
  return dateObj.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function isoDateKey(dateObj) {
  return dateObj.toISOString().slice(0, 10);
}

function firstDayOfMonth(dateObj) {
  return new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
}

function buildCalendarCells(monthDate) {
  const start = firstDayOfMonth(monthDate);
  const dayOfWeek = start.getDay();
  const firstVisible = new Date(start);
  firstVisible.setDate(start.getDate() - dayOfWeek);

  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(firstVisible);
    d.setDate(firstVisible.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function wordCount(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function normalizeForMatch(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function splitStructuredBlocks(text) {
  return String(text || "")
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function dedupeBlocks(blocks) {
  const seen = new Set();
  const out = [];
  blocks.forEach((block) => {
    const key = normalizeForMatch(block).slice(0, 260);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(block);
  });
  return out;
}

function paragraphHasHighlight(paragraph, highlights = []) {
  const p = normalizeForMatch(paragraph);
  if (!p) return false;
  return highlights.some((h) => {
    const key = normalizeForMatch(h).slice(0, 70);
    if (!key) return false;
    return p.includes(key.slice(0, Math.min(42, key.length)));
  });
}

function calculateGameState(plan, selectedDay) {
  const completion = Number(plan?.completion?.percentage || 0);
  const completedDays = Number(plan?.completion?.completedDays || 0);
  const level = Math.max(1, Math.floor(completion / 10) + 1);
  const xp = completion * 12 + completedDays * 5;
  const nextLevelAt = level * 120;
  const streak = Math.max(1, Math.min(30, Math.floor(completedDays / 2) + 1));
  const notesCount = Array.isArray(selectedDay?.dayContent?.stickyNotes) ? selectedDay.dayContent.stickyNotes.length : 0;
  const quests = [
    {
      label: "Open today's learning page",
      done: Boolean(selectedDay?._id)
    },
    {
      label: "Read complete solution",
      done: wordCount(selectedDay?.dayContent?.fullSolution || "") >= 700
    },
    {
      label: "Save at least 1 sticky note",
      done: notesCount >= 1
    },
    {
      label: "Review 3 highlights",
      done: (selectedDay?.dayContent?.importantHighlights || []).length >= 3
    }
  ];
  const questsDone = quests.filter((q) => q.done).length;
  return { level, xp, nextLevelAt, streak, notesCount, quests, questsDone };
}

function HkDevPage({ apiBaseUrl }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [userId, setUserId] = useState("demo-user");
  const [title, setTitle] = useState("My Book Plan");
  const [months, setMonths] = useState(1);
  const [startDate, setStartDate] = useState(today);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDayId, setSelectedDayId] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [updatingDayId, setUpdatingDayId] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [memorySummary, setMemorySummary] = useState("");
  const [aiStatus, setAiStatus] = useState(null);
  const [stickyInput, setStickyInput] = useState("");
  const [stickyColor, setStickyColor] = useState("yellow");
  const [stickySaving, setStickySaving] = useState(false);
  const [stickyError, setStickyError] = useState("");

  const parseApiResponse = async (response) => {
    const raw = await response.text();
    let data = {};
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch (_error) {
        data = {};
      }
    }

    if (!response.ok || !data.ok) {
      const htmlLike = raw.trim().startsWith("<!DOCTYPE") || raw.trim().startsWith("<html");
      if (htmlLike) {
        throw new Error(
          "Backend returned HTML instead of JSON. Check REACT_APP_BACKEND_URL and redeploy backend."
        );
      }
      throw new Error(data.message || "API request failed.");
    }

    return data;
  };

  const loadPlans = async () => {
    setError("");
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/study-plans?userId=${encodeURIComponent(userId.trim() || "demo-user")}`
      );
      const data = await parseApiResponse(response);
      setPlans(data.plans || []);
      if (!selectedPlanId && data.plans?.length) {
        setSelectedPlanId(data.plans[0].id);
      }
    } catch (err) {
      setError(err.message || "Unable to load plans.");
    }
  };

  const loadHealth = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/health`);
      const data = await response.json();
      setAiStatus(data.ai || null);
    } catch (_error) {
      setAiStatus(null);
    }
  };

  const loadChat = async (planId) => {
    if (!planId) return;
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/study-plans/${planId}/chat?userId=${encodeURIComponent(userId.trim() || "demo-user")}`
      );
      const data = await parseApiResponse(response);
      setChatMessages(data.chat?.messages || []);
      setMemorySummary(data.chat?.memorySummary || "");
    } catch (err) {
      setError(err.message || "Unable to load chat memory.");
    }
  };

  const loadPlanDetails = async (planId) => {
    if (!planId) {
      setSelectedPlan(null);
      setSelectedDay(null);
      setSelectedDayId("");
      return;
    }
    setError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/study-plans/${planId}`);
      const data = await parseApiResponse(response);
      setSelectedPlan(data.plan);
      setSelectedDay(null);
      setSelectedDayId("");
      if (data.plan?.startDate) {
        setCurrentMonth(firstDayOfMonth(new Date(`${data.plan.startDate}T00:00:00`)));
      }
      await loadChat(planId);
    } catch (err) {
      setError(err.message || "Unable to load selected plan.");
    }
  };

  const loadDayDetails = async (dayId) => {
    if (!selectedPlan?.id || !dayId) return;
    setDayLoading(true);
    setError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/study-plans/${selectedPlan.id}/days/${dayId}`);
      const data = await parseApiResponse(response);
      setSelectedDay(data.day);
      setSelectedDayId(dayId);
      setStickyError("");
      setStickyInput("");
      setSelectedPlan((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          schedule: prev.schedule.map((item) => (item._id === dayId ? { ...item, ...data.day } : item))
        };
      });
    } catch (err) {
      setError(err.message || "Unable to load day content.");
    } finally {
      setDayLoading(false);
    }
  };

  useEffect(() => {
    loadHealth();
    loadPlans();
  }, []);

  useEffect(() => {
    loadPlanDetails(selectedPlanId);
  }, [selectedPlanId]);

  const handleCreatePlan = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!files.length) {
      setError("Please upload at least one book.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("userId", userId.trim() || "demo-user");
      formData.append("title", title.trim() || "My Book Plan");
      formData.append("months", String(months));
      formData.append("startDate", startDate);
      files.forEach((file) => formData.append("books", file));

      const response = await fetch(`${apiBaseUrl}/api/study-plans`, {
        method: "POST",
        body: formData
      });

      const data = await parseApiResponse(response);
      const createdPlan = data.plan;
      setMessage("Study calendar generated successfully.");
      setSelectedPlan(createdPlan);
      setSelectedPlanId(createdPlan.id);
      setFiles([]);
      await loadPlans();
    } catch (err) {
      setError(err.message || "Could not create plan.");
    } finally {
      setLoading(false);
    }
  };

  const toggleDayProgress = async (dayId, completed) => {
    if (!selectedPlan?.id) return;
    setUpdatingDayId(dayId);
    setError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/study-plans/${selectedPlan.id}/progress`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dayId, completed })
      });
      const data = await parseApiResponse(response);
      setSelectedPlan(data.plan);
      setPlans((current) =>
        current.map((plan) =>
          plan.id === data.plan.id ? { ...plan, completion: data.plan.completion } : plan
        )
      );
      if (selectedDayId) {
        const latestDay = data.plan.schedule.find((d) => d._id === selectedDayId);
        if (latestDay) setSelectedDay((prev) => ({ ...prev, ...latestDay }));
      }
    } catch (err) {
      setError(err.message || "Could not update progress.");
    } finally {
      setUpdatingDayId("");
    }
  };

  const sendChat = async (event) => {
    event.preventDefault();
    const question = chatInput.trim();
    if (!question || !selectedPlan?.id) return;

    setChatLoading(true);
    setError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/study-plans/${selectedPlan.id}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: userId.trim() || "demo-user",
          question,
          dayId: selectedDayId || ""
        })
      });
      const data = await parseApiResponse(response);
      setChatMessages(data.messages || []);
      setMemorySummary(data.memorySummary || "");
      setChatInput("");
    } catch (err) {
      setError(err.message || "Chat failed.");
    } finally {
      setChatLoading(false);
    }
  };

  const updateDayInState = (updatedDay) => {
    setSelectedDay(updatedDay);
    setSelectedPlan((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        schedule: prev.schedule.map((item) => (item._id === updatedDay._id ? { ...item, ...updatedDay } : item))
      };
    });
  };

  const saveStickyNote = async (action, payload = {}) => {
    if (!selectedPlan?.id || !selectedDayId) return;
    setStickySaving(true);
    setStickyError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/study-plans/${selectedPlan.id}/days/${selectedDayId}/sticky-notes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, ...payload })
      });
      const data = await parseApiResponse(response);
      if (data.day) {
        updateDayInState(data.day);
      }
      if (action === "add") {
        setStickyInput("");
      }
    } catch (err) {
      setStickyError(err.message || "Could not update sticky notes.");
    } finally {
      setStickySaving(false);
    }
  };

  const scheduleByDate = useMemo(() => {
    const map = new Map();
    if (!selectedPlan?.schedule) return map;
    selectedPlan.schedule.forEach((day) => {
      map.set(day.date, day);
    });
    return map;
  }, [selectedPlan]);

  const calendarCells = useMemo(() => buildCalendarCells(currentMonth), [currentMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  return (
    <div className="hk-page">
      <header className="hk-header">
        <h1>/hk/dev Exam Preparation Planner</h1>
        <p>
          Upload books, get intelligent day-wise calendar planning, track progress, and chat with
          memory-aware AI tutor powered by semantic document understanding.
        </p>
      </header>

      <section className="hk-panel">
        <h2>Create Plan</h2>
        <form className="hk-form" onSubmit={handleCreatePlan}>
          <label>
            User ID
            <input value={userId} onChange={(e) => setUserId(e.target.value)} />
          </label>
          <label>
            Plan Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            Finish In (months)
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 9, 12].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Start Date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            Upload Books (PDF/TXT/MD/CSV/JSON/XML)
            <input
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              accept=".pdf,.txt,.md,.markdown,.csv,.json,.xml,text/plain,application/pdf"
            />
          </label>
          {!!files.length && (
            <ul className="hk-file-list">
              {files.map((file) => (
                <li key={`${file.name}-${file.size}`}>{file.name}</li>
              ))}
            </ul>
          )}
          <button type="submit" disabled={loading}>
            {loading ? "Generating..." : "Generate Intelligent Calendar"}
          </button>
        </form>
      </section>

      <section className="hk-panel">
        <div className="hk-panel-head">
          <h2>Your Plans</h2>
          <button type="button" onClick={loadPlans}>
            Refresh
          </button>
        </div>
        <div className="hk-plan-select-row">
          <select value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)}>
            <option value="">Select plan</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.title} ({plan.completion?.percentage || 0}%)
              </option>
            ))}
          </select>
        </div>
      </section>

      {message && <p className="hk-ok">{message}</p>}
      {error && <p className="hk-error">{error}</p>}
      {aiStatus?.lastError ? (
        <p className="hk-error">AI runtime status: {aiStatus.lastError}</p>
      ) : (
        <p className="hk-ok">
          AI model: {aiStatus?.model || "n/a"} {aiStatus?.embedModel ? `| Embeddings: ${aiStatus.embedModel}` : ""}
        </p>
      )}

      {selectedPlan && (
        <section className="hk-panel">
          <h2>{selectedPlan.title}</h2>
          <p>
            {selectedPlan.totalBooks} book(s), {selectedPlan.totalSections} sections,{" "}
            {selectedPlan.totalDays} days, {selectedPlan.indexedChunks || 0} indexed passages. Completion:{" "}
            {selectedPlan.completion?.percentage || 0}%.
          </p>
          {(() => {
            const game = calculateGameState(selectedPlan, selectedDay);
            return (
              <div className="hk-game-wrap">
                <div className="hk-game-stats">
                  <div className="hk-game-card xp">
                    <strong>Level {game.level}</strong>
                    <span>
                      XP {game.xp}/{game.nextLevelAt}
                    </span>
                  </div>
                  <div className="hk-game-card streak">
                    <strong>{game.streak} Day Streak</strong>
                    <span>Keep momentum</span>
                  </div>
                  <div className="hk-game-card notes">
                    <strong>{game.notesCount} Notes</strong>
                    <span>Your strategy bank</span>
                  </div>
                </div>
                <div className="hk-quests">
                  <h4>Daily Quests</h4>
                  <p>
                    {game.questsDone}/{game.quests.length} completed
                  </p>
                  <ul>
                    {game.quests.map((q, idx) => (
                      <li key={`quest-${idx}`} className={q.done ? "done" : ""}>
                        {q.done ? "✓" : "○"} {q.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}

          <div className="hk-calendar-wrap">
            <div className="hk-calendar-head">
              <button type="button" onClick={handlePrevMonth}>
                Prev
              </button>
              <h3>{monthTitle(currentMonth)}</h3>
              <button type="button" onClick={handleNextMonth}>
                Next
              </button>
            </div>

            <div className="hk-calendar-grid hk-weekdays">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="hk-weekday">
                  {d}
                </div>
              ))}
            </div>

            <div className="hk-calendar-grid">
              {calendarCells.map((cellDate) => {
                const key = isoDateKey(cellDate);
                const day = scheduleByDate.get(key);
                const inMonth = cellDate.getMonth() === currentMonth.getMonth();
                return (
                  <div
                    key={key}
                    className={`hk-day-cell ${inMonth ? "" : "muted"} ${
                      day?._id && day._id === selectedDayId ? "active" : ""
                    }`}
                  >
                    <div className="hk-day-cell-top">
                      <span>{cellDate.getDate()}</span>
                      {day && (
                        <input
                          type="checkbox"
                          checked={Boolean(day.completed)}
                          disabled={updatingDayId === day._id}
                          onChange={(e) => toggleDayProgress(day._id, e.target.checked)}
                        />
                      )}
                    </div>
                    {day ? (
                      <button
                        type="button"
                        className="hk-cell-task"
                        onClick={() => loadDayDetails(day._id)}
                        title={day.title}
                      >
                        D{day.dayIndex}: {day.title}
                      </button>
                    ) : (
                      <span className="hk-no-task">No task</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hk-day-content">
            <h3>Daily Learning Page</h3>
            {dayLoading && <p>Generating intelligent content...</p>}
            {!dayLoading && !selectedDay && <p>Select a calendar task day to open its study page.</p>}
            {!dayLoading && selectedDay && (
              <article className="hk-day-article">
                {(() => {
                  const quantitative = selectedDay.dayContent?.quantitative || {};
                  const chartData = Array.isArray(quantitative.chartData) ? quantitative.chartData : [];
                  const importantHighlights = dedupeBlocks(selectedDay.dayContent?.importantHighlights || []);
                  const readingBlocks = dedupeBlocks(splitStructuredBlocks(selectedDay.dayContent?.readingContent || ""));
                  const solutionBlocks = dedupeBlocks(splitStructuredBlocks(selectedDay.dayContent?.fullSolution || ""));
                  const modelAnswerBlocks = dedupeBlocks(
                    splitStructuredBlocks(selectedDay.dayContent?.cssExamSolution?.modelAnswer || "")
                  );
                  const maxChartValue = chartData.length
                    ? Math.max(...chartData.map((item) => Number(item.value || 0)), 1)
                    : 1;
                  return (
                    <>
                <header>
                  <h4>{selectedDay.dayContent?.generatedTitle || selectedDay.title}</h4>
                  <p>
                    Day {selectedDay.dayIndex} - {formatDate(selectedDay.date)}
                  </p>
                </header>

                {selectedDay.dayContent?.summary && (
                  <section>
                    <h5>Overview</h5>
                    <p>{selectedDay.dayContent.summary}</p>
                  </section>
                )}

                {!!importantHighlights.length && (
                  <section>
                    <h5>Important Highlights</h5>
                    <div className="hk-highlight-grid">
                      {importantHighlights.map((item, idx) => (
                        <article key={`hl-${idx}`} className="hk-highlight-card">
                          <strong>Highlight {idx + 1}</strong>
                          <p>{item}</p>
                        </article>
                      ))}
                    </div>
                  </section>
                )}

                {!!selectedDay.dayContent?.objectives?.length && (
                  <section>
                    <h5>Learning Objectives</h5>
                    <ul>
                      {selectedDay.dayContent.objectives.map((item, idx) => (
                        <li key={`obj-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {!!selectedDay.dayContent?.keyPoints?.length && (
                  <section>
                    <h5>Key Points</h5>
                    <ul>
                      {selectedDay.dayContent.keyPoints.map((item, idx) => (
                        <li key={`kp-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {selectedDay.dayContent?.readingContent && (
                  <section>
                    <h5>Reading Content</h5>
                    <div className="hk-structured-text">
                      {readingBlocks.map((block, idx) => (
                        <p key={`rc-p-${idx}`} className="hk-reading-text hk-doc-paragraph">
                          {block}
                        </p>
                      ))}
                    </div>
                  </section>
                )}

                {!!selectedDay.dayContent?.sources?.length && (
                  <section>
                    <h5>Source Passages</h5>
                    <div className="hk-source-list">
                      {selectedDay.dayContent.sources.map((item, idx) => (
                        <article key={`src-${idx}`} className="hk-source-card">
                          <div className="hk-source-head">
                            <strong>{item.sectionTitle}</strong>
                            <small>{item.bookName}</small>
                          </div>
                          <p>{item.excerpt}</p>
                        </article>
                      ))}
                    </div>
                  </section>
                )}

                <section>
                  <h5>Sticky Notes</h5>
                  {stickyError && <p className="hk-error">{stickyError}</p>}
                  <div className="hk-sticky-form">
                    <textarea
                      value={stickyInput}
                      onChange={(e) => setStickyInput(e.target.value)}
                      placeholder="Add quick revision note, formula, quote, or reminder..."
                      rows={2}
                    />
                    <div className="hk-sticky-controls">
                      <select value={stickyColor} onChange={(e) => setStickyColor(e.target.value)}>
                        <option value="yellow">Yellow</option>
                        <option value="blue">Blue</option>
                        <option value="green">Green</option>
                        <option value="pink">Pink</option>
                      </select>
                      <button
                        type="button"
                        disabled={stickySaving || !stickyInput.trim()}
                        onClick={() =>
                          saveStickyNote("add", {
                            text: stickyInput,
                            color: stickyColor
                          })
                        }
                      >
                        {stickySaving ? "Saving..." : "Add Note"}
                      </button>
                    </div>
                  </div>
                  {!!selectedDay.dayContent?.stickyNotes?.length && (
                    <div className="hk-sticky-grid">
                      {selectedDay.dayContent.stickyNotes.map((note) => (
                        <article key={note.noteId} className={`hk-sticky-note ${note.color || "yellow"}`}>
                          <p>{note.text}</p>
                          <div className="hk-sticky-actions">
                            <button
                              type="button"
                              disabled={stickySaving}
                              onClick={() => saveStickyNote("togglePin", { noteId: note.noteId })}
                            >
                              {note.pinned ? "Unpin" : "Pin"}
                            </button>
                            <button
                              type="button"
                              disabled={stickySaving}
                              onClick={() => saveStickyNote("delete", { noteId: note.noteId })}
                            >
                              Delete
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>

                {!!selectedDay.dayContent?.tasks?.length && (
                  <section>
                    <h5>Action Tasks</h5>
                    <ul>
                      {selectedDay.dayContent.tasks.map((item, idx) => (
                        <li key={`task-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {!!selectedDay.dayContent?.revisionQuestions?.length && (
                  <section>
                    <h5>Revision Questions</h5>
                    <ul>
                      {selectedDay.dayContent.revisionQuestions.map((item, idx) => (
                        <li key={`rq-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {!!selectedDay.dayContent?.memoryHints?.length && (
                  <section>
                    <h5>Memory Hints</h5>
                    <ul>
                      {selectedDay.dayContent.memoryHints.map((item, idx) => (
                        <li key={`mh-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {!!selectedDay.dayContent?.additionalPoints?.length && (
                  <section>
                    <h5>Augmented Insights</h5>
                    <ul>
                      {selectedDay.dayContent.additionalPoints.map((item, idx) => (
                        <li key={`add-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {selectedDay.dayContent?.fullSolution && (
                  <section>
                    <h5>Complete Study Solution</h5>
                    <p>
                      <strong>Length:</strong> {wordCount(selectedDay.dayContent.fullSolution)} words
                    </p>
                    <div className="hk-structured-text">
                      {solutionBlocks.map((block, idx) => {
                        const isHeading = block.startsWith("## ");
                        if (isHeading) {
                          return (
                            <h6 key={`fs-h-${idx}`} className="hk-doc-heading">
                              {block.replace(/^##\s*/, "")}
                            </h6>
                          );
                        }
                        const highlighted = paragraphHasHighlight(
                          block,
                          importantHighlights
                        );
                        return (
                          <p
                            key={`fs-p-${idx}`}
                            className={`hk-reading-text hk-doc-paragraph ${highlighted ? "highlighted" : ""}`}
                          >
                            {block}
                          </p>
                        );
                      })}
                    </div>
                  </section>
                )}

                {selectedDay.dayContent?.cssExamSolution?.likelyQuestion && (
                  <section>
                    <h5>CSS/PMS Answer Builder</h5>
                    <p>
                      <strong>Likely Question:</strong> {selectedDay.dayContent.cssExamSolution.likelyQuestion}
                    </p>
                    {!!selectedDay.dayContent.cssExamSolution.structure?.length && (
                      <ul>
                        {selectedDay.dayContent.cssExamSolution.structure.map((item, idx) => (
                          <li key={`st-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {selectedDay.dayContent.cssExamSolution.modelAnswer && (
                      <>
                        <p>
                          <strong>Model Answer Length:</strong>{" "}
                          {wordCount(selectedDay.dayContent.cssExamSolution.modelAnswer)} words
                        </p>
                        <div className="hk-structured-text">
                          {modelAnswerBlocks.map((block, idx) => (
                            <p key={`ma-p-${idx}`} className="hk-reading-text hk-doc-paragraph">
                              {block}
                            </p>
                          ))}
                        </div>
                      </>
                    )}
                  </section>
                )}

                {!!selectedDay.dayContent?.solvedQuestions?.length && (
                  <section>
                    <h5>Solved Practice Questions</h5>
                    <div className="hk-solved-list">
                      {selectedDay.dayContent.solvedQuestions.map((item, idx) => (
                        <article key={`sq-${idx}`} className="hk-solved-card">
                          <h6>Q{idx + 1}. {item.question}</h6>
                          <p>{item.answer}</p>
                        </article>
                      ))}
                    </div>
                  </section>
                )}

                <section>
                  <h5>Quantitative Snapshot</h5>
                  <div className="hk-metrics-grid">
                    <div className="hk-metric-card">
                      <strong>{quantitative.totalWords || 0}</strong>
                      <span>Words Covered</span>
                    </div>
                    <div className="hk-metric-card">
                      <strong>{quantitative.estimatedMinutes || 0}</strong>
                      <span>Estimated Minutes</span>
                    </div>
                    <div className="hk-metric-card">
                      <strong>{quantitative.sectionsCount || 0}</strong>
                      <span>Sections</span>
                    </div>
                    <div className="hk-metric-card">
                      <strong>{quantitative.passagesCount || 0}</strong>
                      <span>Evidence Passages</span>
                    </div>
                  </div>
                </section>

                {!!quantitative.sectionTable?.length && (
                  <section>
                    <h5>Section Study Table</h5>
                    <div className="hk-table-wrap">
                      <table className="hk-table">
                        <thead>
                          <tr>
                            <th>Section</th>
                            <th>Words</th>
                            <th>Minutes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quantitative.sectionTable.map((row, idx) => (
                            <tr key={`tbl-${idx}`}>
                              <td>{row.sectionTitle}</td>
                              <td>{row.words}</td>
                              <td>{row.estimatedMinutes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {!!chartData.length && (
                  <section>
                    <h5>Workload Graph (Words by Section)</h5>
                    <div className="hk-chart">
                      {chartData.map((item, idx) => {
                        const value = Number(item.value || 0);
                        const width = Math.max(4, Math.round((value / maxChartValue) * 100));
                        return (
                          <div key={`bar-${idx}`} className="hk-chart-row">
                            <span className="hk-chart-label">{item.label}</span>
                            <div className="hk-chart-track">
                              <div className="hk-chart-bar" style={{ width: `${width}%` }} />
                            </div>
                            <span className="hk-chart-value">{value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {!!quantitative.numericHighlights?.length && (
                  <section>
                    <h5>Numeric Mentions Found in Text</h5>
                    <ul>
                      {quantitative.numericHighlights.map((item, idx) => (
                        <li key={`num-${idx}`}>
                          <strong>{item.value}:</strong> {item.context}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {!!selectedDay.dayContent?.references?.length && (
                  <section>
                    <h5>Latest Study References (Google News RSS)</h5>
                    <div className="hk-table-wrap">
                      <table className="hk-table">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Source</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedDay.dayContent.references.map((item, idx) => (
                            <tr key={`ref-${idx}`}>
                              <td>
                                <a href={item.link} target="_blank" rel="noreferrer">
                                  {item.title}
                                </a>
                              </td>
                              <td>{item.source || "Google News"}</td>
                              <td>{item.publishedAt || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
                    </>
                  );
                })()}
              </article>
            )}
          </div>

          <div className="hk-chat">
            <h3>Subject Chat (Memory-Aware)</h3>
            <p className="hk-chat-memory">
              <strong>Memory:</strong> {memorySummary || "Building memory as you chat..."}
            </p>
            <div className="hk-chat-box">
              {chatMessages.length === 0 && <p className="hk-chat-empty">Ask anything about your subject.</p>}
              {chatMessages.map((msg) => (
                <div key={msg._id} className={`hk-chat-msg ${msg.role}`}>
                  <p>{msg.content}</p>
                </div>
              ))}
            </div>
            <form className="hk-chat-form" onSubmit={sendChat}>
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask about concepts, summaries, exam strategy, or today's topic..."
                rows={3}
              />
              <button type="submit" disabled={chatLoading}>
                {chatLoading ? "Thinking..." : "Ask AI Tutor"}
              </button>
            </form>
          </div>
        </section>
      )}
    </div>
  );
}

export default HkDevPage;
